import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PatientsState } from "../interfaces/Patients";
import apiClient from "../api/apiClient";

interface ProfileState {
  loading: boolean;
  user: PatientsState | null;
  error: string | null;
  isLoggedIn: boolean;
  data: PatientsState[] | null;
}

const initialState: ProfileState = {
  loading: false,
  user: null,
  error: null,
  isLoggedIn: false,
  data: null,
};

const syncLocalStorageUser = (user: any) => {
  if (!user || typeof user !== "object") return;
  try {
    localStorage.setItem("profile", JSON.stringify(user));
    const storedLogin = localStorage.getItem("loginData");
    if (storedLogin) {
      const parsed = JSON.parse(storedLogin);
      if (parsed && typeof parsed === "object") {
        parsed.user = { ...(parsed.user || {}), ...user };
        localStorage.setItem("loginData", JSON.stringify(parsed));
      }
    }
  } catch (err) {
    console.error("Error syncing profile to localStorage:", err);
  }
};

let activeProfilePromise: Promise<any> | null = null;
let lastProfileFetchTime = 0;

export const ProfileDetails = createAsyncThunk(
  "profile/ProfileDetails",
  async (force: boolean | undefined, thunkAPI) => {
    if (activeProfilePromise) {
      return activeProfilePromise;
    }
    activeProfilePromise = (async () => {
      try {
        const response = await apiClient.get("/api/v1/patients/profile");
        const user = response.data?.data?.user;
        syncLocalStorageUser(user);
        lastProfileFetchTime = Date.now();
        return user;
      } catch (error: any) {
        return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
      } finally {
        activeProfilePromise = null;
      }
    })();
    return activeProfilePromise;
  },
  {
    condition: (force, { getState }) => {
      if (force === true) return true;
      const state: any = getState();
      // If a request is currently in-flight, reuse it instead of launching a new one
      if (activeProfilePromise) {
        return false;
      }
      // If already fetched in the last 15 seconds, skip duplicate call
      if (Date.now() - lastProfileFetchTime < 15000 && state.profile?.user) {
        return false;
      }
      return true;
    },
  }
);

export const UpdateProfile = createAsyncThunk(
  "profile/UpdateProfile",
  async (updatedProfileData: any, thunkAPI) => {
    try {
      const response = await apiClient.put(
        "/api/v1/patients/profile",
        updatedProfileData,
      );
      const user = response.data?.data?.user;
      syncLocalStorageUser(user);
      return user;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Update failed: ${error.message}`);
    }
  },
);

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(ProfileDetails.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset error on pending
      })
      .addCase(ProfileDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.data = action.payload;
      })
      .addCase(ProfileDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(UpdateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(UpdateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.data = action.payload;
      })
      .addCase(UpdateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error("Error updating profile:", action.payload);
      });
  },
});

export const { resetState } = profileSlice.actions;
export default profileSlice.reducer;
